import * as React from "react"
import {
  type CategoryType,
  CsvExportFieldName,
  DirectionFromOrigin,
  type ExportTransactionsQueryVariables,
  MissingObject,
  SortDirection,
  TaxStatus,
  TransactionColumnName,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useSession from "@digits-shared/hooks/useSession"
import { usePartyRoles } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/usePartyRoles"
import { useVendorCustomerCustomization } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/useVendorCustomerCustomization"
import { isMissingParty } from "src/frontend/components/OS/Applications/VendorDirectory/moleculeHydrators/filteredVendors/filteredMissingPartyHydrator"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

// Sort vendor transactions by party name in ascending order and then by transaction date in descending order
const VENDOR_ORDER_PAIRS = [
  { columnName: TransactionColumnName.Party, direction: SortDirection.Ascending },
  { columnName: TransactionColumnName.OccurredAt, direction: SortDirection.Descending },
]

const VENDOR_SUMMARY_FIELDS = [
  { fieldName: CsvExportFieldName.PartyName },
  { fieldName: CsvExportFieldName.TransactionDate },
  { fieldName: CsvExportFieldName.CategoryName },
  { fieldName: CsvExportFieldName.CategoryNumber },
  { fieldName: CsvExportFieldName.SplitCategoryName },
  { fieldName: CsvExportFieldName.SplitCategoryNumber },
  { fieldName: CsvExportFieldName.TransactionAmount },
  { fieldName: CsvExportFieldName.Memo },
]

const VENDOR_TAXES_FIELDS = [
  { fieldName: CsvExportFieldName.PartyName },
  { fieldName: CsvExportFieldName.PartyEntityType },
  { fieldName: CsvExportFieldName.PartyW9Status },
  { fieldName: CsvExportFieldName.TransactionDate },
  { fieldName: CsvExportFieldName.CategoryName },
  { fieldName: CsvExportFieldName.CategoryNumber },
  { fieldName: CsvExportFieldName.SplitCategoryName },
  { fieldName: CsvExportFieldName.SplitCategoryNumber },
  { fieldName: CsvExportFieldName.TaxStatus },
  { fieldName: CsvExportFieldName.TransactionAmount },
  { fieldName: CsvExportFieldName.Memo },
]

export enum ExportTransactionsType {
  Ledger = "Ledger",
  VendorsSummaryTab = "VendorsSummaryTab",
  VendorsTaxesTab = "VendorsTaxesTab",
  Analysis = "Analysis",
}

interface LedgerExport {
  categoryId?: string
  toVault: boolean
  type: ExportTransactionsType.Ledger
}
interface VendorsSummaryTabExport {
  partyId?: string
  toVault: boolean
  type: ExportTransactionsType.VendorsSummaryTab
}
interface VendorsTaxesTabExport {
  partyId?: string
  toVault: boolean
  type: ExportTransactionsType.VendorsTaxesTab
}
interface AnalysisExport {
  categoryTypes?: CategoryType[]
  toVault: boolean
  type: ExportTransactionsType.Analysis
}

type UseExportToCsvProps =
  | LedgerExport
  | VendorsSummaryTabExport
  | VendorsTaxesTabExport
  | AnalysisExport

type ExportFilterProps = Omit<UseExportToCsvProps, "type" | "toVault">
export interface ExportPropsWithoutToVault extends Omit<UseExportToCsvProps, "toVault"> {}
export type InlineMoreOptionsButtonProps =
  | Required<Omit<LedgerExport, "toVault">>
  | Required<Omit<VendorsSummaryTabExport, "toVault">>
  | Required<Omit<VendorsTaxesTabExport, "toVault">>
  | Required<Omit<AnalysisExport, "toVault">>

export function useExportVariables({
  type,
  toVault,
  ...props
}: UseExportToCsvProps): ExportTransactionsQueryVariables {
  let getVariables: (props: ExportFilterProps) => ExportTransactionsQueryVariables
  const ledgerVariables = useLedgerVariablesFn(toVault)
  const vendorsSummaryTabVariables = useVendorsSummaryTabVariablesFn(toVault)
  const vendorsTaxesTabVariables = useVendorsTaxesTabVariablesFn(toVault)
  const analysisVariables = useAnalysisVariablesFn(toVault)

  switch (type) {
    case ExportTransactionsType.VendorsSummaryTab:
      getVariables = vendorsSummaryTabVariables
      break
    case ExportTransactionsType.VendorsTaxesTab:
      getVariables = vendorsTaxesTabVariables
      break
    case ExportTransactionsType.Analysis:
      getVariables = analysisVariables
      break
    default:
      getVariables = ledgerVariables
  }

  return getVariables(props)
}

function useLedgerVariablesFn(
  toVault?: boolean
): ({ categoryId }: Omit<LedgerExport, "type">) => ExportTransactionsQueryVariables {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()

  return React.useCallback(
    ({ categoryId }: Omit<LedgerExport, "type">) =>
      ({
        filter: {
          viewKey,
          categoryId,
          withoutCategoryAncestors: true,
        },
        direction: DirectionFromOrigin.Past,
        origin: {
          ...intervalOrigin,
          intervalCount: intervalOrigin.intervalCount || 1,
        },
        temporary: !toVault,
      }) as ExportTransactionsQueryVariables,
    [intervalOrigin, toVault, viewKey]
  )
}

function useVendorsSummaryTabVariablesFn(
  toVault?: boolean
): ({ partyId }: Omit<VendorsSummaryTabExport, "type">) => ExportTransactionsQueryVariables {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const intervalOrigin = useIntervalOrigin()
  const dateRangeString = useIntervalRangeString()
  const partyRoles = usePartyRoles()
  const { isCustomers } = useVendorCustomerCustomization()
  const fileBaseName = isCustomers ? "Customer" : "Vendor"

  const viewKey = useViewVersion()

  return React.useCallback(
    ({ partyId }: Omit<VendorsSummaryTabExport, "type">) =>
      ({
        filter: {
          viewKey,
          partyId: isMissingParty(partyId ?? "") ? undefined : partyId,
          partyRoles,
          missingParty: isMissingParty(partyId ?? "") ? MissingObject.Include : undefined,
        },
        direction: DirectionFromOrigin.Past,
        origin: {
          ...intervalOrigin,
          intervalCount: intervalOrigin.intervalCount || 1,
        },
        orderPairs: VENDOR_ORDER_PAIRS,
        temporary: !toVault,
        fileName: `${currentLegalEntity.name}_${dateRangeString}_Transactions by ${fileBaseName}.csv`,
        fields: VENDOR_SUMMARY_FIELDS,
      }) as ExportTransactionsQueryVariables,
    [
      viewKey,
      partyRoles,
      intervalOrigin,
      toVault,
      currentLegalEntity.name,
      dateRangeString,
      fileBaseName,
    ]
  )
}

function useVendorsTaxesTabVariablesFn(
  toVault?: boolean
): ({ partyId }: Omit<VendorsTaxesTabExport, "type">) => ExportTransactionsQueryVariables {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const intervalOrigin = useIntervalOrigin()
  const dateRangeString = useIntervalRangeString()
  const viewKey = useViewVersion()
  const partyRoles = usePartyRoles()
  const { isCustomers } = useVendorCustomerCustomization()
  const fileBaseName = isCustomers ? "Customers" : "Vendors"

  return React.useCallback(
    ({ partyId }: Omit<VendorsTaxesTabExport, "type">) =>
      ({
        filter: {
          viewKey,
          partyId: isMissingParty(partyId ?? "") ? undefined : partyId,
          partyRoles,
          missingParty: isMissingParty(partyId ?? "") ? MissingObject.Include : undefined,
          taxStatuses: [TaxStatus.Taxable],
        },
        orderPairs: VENDOR_ORDER_PAIRS,
        direction: DirectionFromOrigin.Past,
        origin: {
          ...intervalOrigin,
          intervalCount: intervalOrigin.intervalCount || 1,
        },
        temporary: !toVault,
        fileName: `${currentLegalEntity.name}_${dateRangeString}_1099 ${fileBaseName}_Detailed.csv`,
        fields: VENDOR_TAXES_FIELDS,
      }) as ExportTransactionsQueryVariables,
    [
      viewKey,
      partyRoles,
      intervalOrigin,
      toVault,
      currentLegalEntity.name,
      dateRangeString,
      fileBaseName,
    ]
  )
}

function useAnalysisVariablesFn(
  toVault?: boolean
): ({ categoryTypes }: Omit<AnalysisExport, "type">) => ExportTransactionsQueryVariables {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()

  return React.useCallback(
    ({ categoryTypes }: Omit<AnalysisExport, "type">) =>
      ({
        filter: {
          viewKey,
          categoryTypes,
          withoutCategoryAncestors: false,
        },
        direction: DirectionFromOrigin.Past,
        origin: {
          ...intervalOrigin,
          intervalCount: intervalOrigin.intervalCount || 1,
        },
        temporary: !toVault,
      }) as ExportTransactionsQueryVariables,
    [intervalOrigin, toVault, viewKey]
  )
}

const useIntervalRangeString = () => {
  const intervalOrigin = useIntervalOrigin()

  return React.useMemo(() => {
    const range = dateTimeHelper.dayjsRangeFromIntervalOrigin({
      ...intervalOrigin,
      intervalCount: intervalOrigin.intervalCount || 1,
    })

    return `${range.from.format("YYYY-MM-DD").toString()}_${range.to.format("YYYY-MM-DD").toString()}`
  }, [intervalOrigin])
}
